import { Filter, GlProgram } from 'pixi.js';

/**
 * Ambiance filter (docs/12 M4): one cheap fullscreen pass over the WORLD —
 * soft vignette, a touch of saturation and a slow warm "breathing" pulse.
 * Single texture fetch per pixel, safe for the Xbox Edge target.
 */
const vertex = /* glsl */ `
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
  vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
  position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void) {
  return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
  gl_Position = filterVertexPosition();
  vTextureCoord = filterTextureCoord();
}
`;

const fragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform float uTime;

void main(void) {
  vec4 color = texture(uTexture, vTextureCoord);

  // Normalized screen position (filter frame ≈ full screen for the world)
  vec2 uv = vTextureCoord * uInputSize.xy * uInputSize.zw;
  float d = distance(uv, vec2(0.5));

  // Soft vignette — focus pulls to the action, corners sink away
  float vig = smoothstep(0.92, 0.38, d);
  color.rgb *= mix(0.74, 1.0, vig);

  // Gentle saturation lift + slow breathing pulse
  float luma = dot(color.rgb, vec3(0.299, 0.587, 0.114));
  color.rgb = mix(vec3(luma), color.rgb, 1.07);
  color.rgb *= 0.985 + 0.015 * sin(uTime * 0.7);

  finalColor = color;
}
`;

export class AmbianceFilter {
  readonly filter: Filter;
  private time = 0;

  constructor() {
    this.filter = new Filter({
      glProgram: GlProgram.from({ vertex, fragment, name: 'ambiance-filter' }),
      resources: {
        ambianceUniforms: {
          uTime: { value: 0, type: 'f32' },
        },
      },
    });
  }

  update(dt: number): void {
    this.time += dt;
    const uniforms = this.filter.resources.ambianceUniforms as {
      uniforms: { uTime: number };
    };
    uniforms.uniforms.uTime = this.time;
  }
}
